import { CreditCardFilled, DeleteOutlined, ExclamationCircleFilled, FileExcelOutlined, PlusOutlined, QuestionCircleOutlined, SendOutlined, UploadOutlined } from "@ant-design/icons";
import type { ColDef, GridReadyEvent } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { Button, Input, message, Modal, notification, Popconfirm, Select, Table } from 'antd';
import type { ColumnsType } from "antd/es/table";
import DOMPurify from 'dompurify';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AddFiling, api_ChangeUserID, RemoveFiling, UpdateFiling } from '../api/cudFiling';
import { DownloadExcelFiling } from '../api/exceloutFiling';
import { api_getFiling, FectchFiling } from '../api/FetchFiling';
import { getPaymentUrl } from '../api/getPaymentUrl';
import { PostFiling } from '../api/PostFiling';
import { convertTimezoneToPst, convertToRow, convertToRowFiling, isAddFilingResponse, isFetchFilingResponse, isUploadExcelResponse, parsingCarrier } from '../common';
import CustomLoadingOverlay from "../components/CustomLoadingOverlay";
import DeleteButtonComponent from '../components/DeleteButtonComponent';
import { ExcelFilingRequest, FetchFilingRequest, FilingData, FilingProps, PostFilingRequest, RowFiling, UploadExcelResponse } from '../constant';

import {
  AllCommunityModule,
  CheckboxEditorModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  themeQuartz,
  ValidationModule,
} from "ag-grid-community";
import { DeleteAllFilings } from '../api/deleteall';
import { uploadexcel } from '../api/uploadExcel';
import CustomSelectCellRenderer from "../components/CustomSelectCell";
import CustomSelectCellEditor from '../components/CustomSelectCellEditor';

const { confirm } = Modal;

ModuleRegistry.registerModules([AllCommunityModule, CheckboxEditorModule, ClientSideRowModelModule, ValidationModule]);

const HeaderStyle: React.CSSProperties = {
  height: '50px',
};

const bulkhelpStyle: React.CSSProperties = {
  minHeight: '100px',
  maxHeight: '400px',
  overflowY: 'auto'
};

const NEW_FILING_GRID_SCHEMA_VERSION = 1;
const NEW_FILING_GRID_SCHEMA_KEY = 'gs.newfiling.grid.schema.version';
const NEW_FILING_GRID_LEGACY_STATE_KEYS = [
  'gs.newfiling.grid.columnState',
  'gs.newfiling.grid.filterModel',
  'newFilingGridState',
  'newFilingsGridState',
];

const POLICY_NUMBER_FIELD = 'policynumber';

const normalizePolicyNumberColumn = (colDef: ColDef): ColDef => {
  if (colDef.field !== POLICY_NUMBER_FIELD && colDef.colId !== POLICY_NUMBER_FIELD) {
    return colDef;
  }

  return {
    ...colDef,
    colId: POLICY_NUMBER_FIELD,
    field: POLICY_NUMBER_FIELD,
    headerName: 'Policy Number',
    cellDataType: 'text',
    cellEditor: 'agTextCellEditor',
    // filter: 'agTextColumnFilter',
    valueParser: (params: any) => (params.newValue == null ? '' : String(params.newValue)),
    valueFormatter: undefined,
  };
};

const sanitizeNewFilingColumnDefs = (defs: ColDef[]): ColDef[] => {
  return defs.map((colDef) => normalizePolicyNumberColumn(colDef));
};

const getPolicyColumnSnapshot = (defs: ColDef[]) => {
  const policyCol = defs.find((col) => col.field === POLICY_NUMBER_FIELD || col.colId === POLICY_NUMBER_FIELD);
  if (!policyCol) {
    return null;
  }

  return {
    field: policyCol.field,
    colId: policyCol.colId,
    cellDataType: policyCol.cellDataType,
    cellEditor: policyCol.cellEditor,
    filter: policyCol.filter,
    hasValueFormatter: typeof policyCol.valueFormatter === 'function',
  };
};

const getColumnFingerprint = (defs: ColDef[]) => {
  return defs
    .map((col) => `${col.colId ?? col.field ?? 'unknown'}:${String(col.cellDataType ?? 'auto')}:${String(col.cellEditor ?? 'default')}:${String(col.filter ?? 'default')}`)
    .join('|');
};

const clearLegacyGridStateIfNeeded = () => {
  try {
    const currentVersion = localStorage.getItem(NEW_FILING_GRID_SCHEMA_KEY);
    if (currentVersion === String(NEW_FILING_GRID_SCHEMA_VERSION)) {
      return;
    }

    NEW_FILING_GRID_LEGACY_STATE_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    localStorage.setItem(NEW_FILING_GRID_SCHEMA_KEY, String(NEW_FILING_GRID_SCHEMA_VERSION));
    console.info('[NewFilingGrid] Cleared incompatible persisted grid state', {
      schemaVersion: NEW_FILING_GRID_SCHEMA_VERSION,
      clearedKeys: NEW_FILING_GRID_LEGACY_STATE_KEYS,
    });
  } catch (error) {
    console.warn('[NewFilingGrid] Failed to validate or clear persisted grid state', error);
  }
};

function formatNumber(num: number, maxDecimals = 4) {
  const factor = Math.pow(10, maxDecimals);
  const rounded = Math.round(num * factor) / factor;
  return rounded;
}
// const CustomSelectCellRenderer: React.FC<{ value: string }> = ({ value }) => (
//   <div className="flex items-center justify-between w-full pr-0 pl-2" style={{ height: '100%' }}>
//     <span className="text-sm">{value}</span>
//     <DownOutlined className="text-gray-400 text-xs" />
//   </div>
// );

// const CustomSelectCellRenderer = (props: any) => {
//   const { value, api, node, column } = props;

//   const handleClick = () => {
//     api.startEditingCell({
//       rowIndex: node.rowIndex,
//       colKey: column.getColId(),
//     });
//   };

//   return (
//     <div
//       className={`flex items-center justify-between w-full pl-2 cursor-pointer mt-1`}
//       onClick={handleClick}
//     >
//       <span className="text-xs text-gray-800">
//         {value}
//       </span>
//       <DownOutlined className="text-gray-400 text-xs ml-1" />
//     </div>
//   );
// };

const NewFiling: React.FC<FilingProps> = ({ user, carriers, users, slcodes, isSelected }) => {

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [rowData, setRowData] = useState<RowFiling[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [carrierrows, setCarrierrows] = useState<any[]>([]);
  const [selectValue, setSelectValue] = useState<string>("");
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [filingList, setFilingList] = useState<FilingData[]>([]);
  const listRef = useRef(filingList);
  const [isPostModal, setIsPostModal] = useState(false)
  const [isBulkHelpModal, setIsBulkHelpModal] = useState(false);
  const [bulkhelp_html, setBulkHelp_html] = useState<string>('');
  const [policynumber, setPolicyNumber] = useState<string>("");
  // const [brokerName, setBrokerName] = useState<string>("");
  // const [licenseNumber, setLicenseNumber] = useState<string>("");
  // const [inputMonth, setInputMonth] = useState<string>("");
  // const [inputYear, setInputYear] = useState<string>("");
  // const [inputSignature, setInputSignature] = useState<string>("");
  // const [inputDate, setInputDate] = useState<string>("");
  const [selectUser, setSelectUser] = useState<string>(user.userid.toString());
  const gridRef = useRef<AgGridReact>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [colDefs] = useState<ColDef[]>(sanitizeNewFilingColumnDefs([
      {
        headerClass: 'delete-header',
        headerName: "",
        field: "delete",
        width: 20,
        editable: false,
        cellStyle: { padding: '0', margin: '0' },
        cellRenderer: (params: any) => (
          <DeleteButtonComponent
            api={params.api}
            node={params.node}
            onRowDelete={handleRowDelete}
          />
        ),
      },
      {
        headerClass: 'validate-header',
        headerName: "",
        field: "validate",
        editable: false,
        width: 40,
        tooltipField: "validate",
        tooltipComponent: "customTooltip"
      },
      { headerName: 'Late?', field: "late", width: 60, resizable: true, editable: false },
      { headerName: 'Policy Number', field: "policynumber", width: 150, resizable: true, editable: true, cellStyle: { textAlign: "left" } },
      {
        headerName: 'Trans Type', field: "transType", width: 70, resizable: true,
        editable: true,
        cellEditorParams: {
          values: ['N', 'R', 'E', 'C', 'A', 'X'],
          valueSetter: (params: any) => {
            params.data.transType = params.newValue;
            return true;
          },
        },
        cellEditor: CustomSelectCellEditor,
        cellRenderer:CustomSelectCellRenderer,
        tooltipValueGetter: (params: any) => {
          const { value, data } = params;
          let statusText = '';
          switch (value) {
            case 'N':
              statusText = 'New policy';
              break;
            case 'R':
              statusText = 'Renewal';
              break;
            case 'E':
              statusText = 'Endorsement';
              break;
            case 'C':
              statusText = 'Cancellation';
              break;
            case 'A':
              statusText = 'Audit';
              break;
            case 'X':
              statusText = 'Tax Exempt';
              break;
            default:
              statusText = '';
          }

          return statusText;
        },
      },
      { headerName: 'Insured Name', field: "insuredName", width: 200, resizable: true, editable: true, cellStyle: { textAlign: "left" } },
      { headerName: 'Insured City, State Zip', field: "insuredAddress", width: 250, resizable: true, editable: true, cellStyle: { textAlign: "left" } },
      {
        headerName: 'Transaction Effective Date', field: "transEffectiveDate", width: 100, resizable: true, editable: true, cellEditor: "agDateCellEditor",
        valueFormatter: (params) => {
          const value = params.value;
          return value ? moment(value).format('MM/DD/YYYY') : '';
        },
      },
      {
        headerName: 'Original Policy Date', field: "policyEffectiveDate", width: 100, resizable: true, editable: true, cellEditor: "agDateCellEditor",
        valueFormatter: (params) => {
          const value = params.value;
          return value ? moment(value).format('MM/DD/YYYY') : '';
        },
      },
      {
        headerName: 'Class Code', field: "slCode", width: 270, resizable: true, cellStyle: { textAlign: "left" },
        editable: true,
        cellEditorParams: {
          cellHeight: 20,
          valueListMaxWidth: 250,
          valueListMaxHeight: 300,
          values: Object.values(slcodes),
          valueFormatter: (params: any) => {
            return slcodes[params.value] || params.value;
          },
          valueParser: (newValue: string) => {
            const key = Object.keys(slcodes).find(
              (key) => slcodes[key] === newValue
            );
            return key || newValue;
          },
          valueSetter: (params: any) => {
            params.data.transType = params.newValue;
            return true;
          },
        },
        cellEditor: CustomSelectCellEditor,
        cellRenderer:CustomSelectCellRenderer
      },
      { headerName: 'Premium', field: "premium", width: 90, resizable: true, cellStyle: { textAlign: "right" } },
      { headerName: 'Policy Fees', field: "policyFee", width: 70, resizable: true, cellStyle: { textAlign: "right" } },
      { headerName: 'State Tax (3%)', field: "tax", width: 80, resizable: true, editable: false, cellStyle: { textAlign: "right" } },
      { headerName: 'Stamping Fee (0.2%)', field: "stampingFee", width: 90, resizable: true, editable: false, cellStyle: { textAlign: "right" } },
      {
        headerName: 'Carriers (double click to add or change)',
        field: 'carriers',
        width: 800,
        editable: false,
        cellStyle: { textAlign: "left" }
      },
  ]));

  const CustomTooltip = (props: any) => {
    if (listRef.current[props.rowIndex].warningMessage !== "") {
      return (
        <div
          style={{
            backgroundColor: "rgb(254 249 195 / var(--tw-bg-opacity, 1))",
            whiteSpace: "nowrap",
            padding: "5px",
            maxWidth: "400px !important",
            border: "1px solid #ccc",
            position: "absolute",
            fontSize: "11px",
            zIndex: 1000,
          }}
          dangerouslySetInnerHTML={{ __html: listRef.current[props.rowIndex].warningMessage }}
        >
        </div>
      );
    }
    return null
  };

  const ReloadFiling = (userid: string) => {
    const filingrequest: FetchFilingRequest = {
      userid: Number(userid),
      posted: "N",
      searchWords: "",
      licenseNumber: user.licenseNumber
    }

    setLoading(true)
    setLoadingMessage('Loading...')
    FectchFiling(filingrequest)
      .then((fetchFilingResult) => {
        if (fetchFilingResult.success) {
          if (isFetchFilingResponse(fetchFilingResult)) {
            setFilingList(fetchFilingResult.fetchedList)
            setRowData(convertToRowFiling(fetchFilingResult.fetchedList, slcodes))
          }
        }
        else {
          if ("errorMsg" in fetchFilingResult) {
            message.error({ content: 'Failed to load the Filing.', key: 'FilingStatus', duration: 2 });
          }
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    listRef.current = filingList;
  }, [filingList]);

  useEffect(() => {
    ReloadFiling(user.userid)
  }, []);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      headerClass: 'custom-header',
      editable: true,
      cellDataType: false,
      sortable: false,
      enableCellChangeFlash: true,
      tooltipComponentParams: {
        style: {
          maxWidth: "400px",
        },
      },
    };
  }, []);

  const myTheme = themeQuartz
    .withParams({
      headerColumnBorder: { color: 'gray' },
      headerColumnBorderHeight: '90%',
    });

  const onGridReady = (event: GridReadyEvent) => {
    clearLegacyGridStateIfNeeded();

    const runtimeDefs = (event.api.getColumnDefs() ?? []) as ColDef[];
    const sanitizedDefs = sanitizeNewFilingColumnDefs(runtimeDefs.length ? runtimeDefs : colDefs);
    event.api.setGridOption('columnDefs', sanitizedDefs);

    const policySnapshot = getPolicyColumnSnapshot(sanitizedDefs);
    console.info('[NewFilingGrid] Final columnDefs snapshot', {
      userId: user.userid,
      totalColumns: sanitizedDefs.length,
      policyColumn: policySnapshot,
      columnFingerprint: getColumnFingerprint(sanitizedDefs),
    });
  };

  const onCellValueChanged = (params: any) => {

    const key = Object.keys(slcodes).find(
      (key) => slcodes[key] === params.data.slCode
    );
    console.log(`call onCellValueChanged: ${params.oldValue} ${params.colDef.field}`)

    const updateRow = filingList[params.rowIndex]
    updateRow.policyNumber = params.data.policynumber
    updateRow.transType = params.data.transType
    updateRow.insuredName = params.data.insuredName
    updateRow.insuredAddress = params.data.insuredAddress
    if (params.colDef.field === "transEffectiveDate") {
      updateRow.transEffectiveDate = new Date(params.data.transEffectiveDate)
    }
    if (params.colDef.field === "policyEffectiveDate"){
      updateRow.policyEffectiveDate = new Date(params.data.policyEffectiveDate)
    }
    updateRow.slCode = key || ""
    updateRow.premium = Number(params.data.premium)
    updateRow.policyFee = Number(params.data.policyFee)
    updateRow.tax = Number(params.data.tax)
    updateRow.stampingFee = Number(params.data.stampingFee)

    UpdateFiling(updateRow)
      .then((AddFilingResult) => {
        if (AddFilingResult.success) {
          if (isAddFilingResponse(AddFilingResult)) {
            const updatedfilinglist = [...filingList];
            updatedfilinglist[params.rowIndex] = AddFilingResult.newfiling
            setFilingList(updatedfilinglist);
            //setRowData(convertToRowFiling(updatedfilinglist, slcodes))
            const rowNode = gridRef.current!.api.getDisplayedRowAtIndex(params.rowIndex)
            rowNode?.setData(convertToRow(AddFilingResult.newfiling, slcodes))
          }
        }
        else {
          if ("errorMsg" in AddFilingResult) {
            message.error(AddFilingResult.errorMsg)
          }
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
        return
      });
  };

  const onCellDoubleClicked = (params: any) => {
    if (params.colDef.field === "carriers") {
      setCurrentRowIndex(params.rowIndex)
      showModal(params.data.carriers)
    }
  };

  const handleBulkHelpCancel = () => {
    setIsBulkHelpModal(false);
  }


  const showModal = (data: string) => {
    const initrow = data === "" ? [] : data
      .split(" / ")
      .map((item, index) => {
        console.log(item)
        const match = parsingCarrier(item)
        if (match) {
          return { key: index, carrier: match[0], percent: match[1] };
        }
        return [];
      })

    setCarrierrows(initrow)
    setIsModalVisible(true);
  }

  const handleCancel = () => {
    console.log('modal cancel')
    setIsModalVisible(false);
    setCarrierrows([]);
  }
  const handleAddRow = () => {
    if (!selectValue) {
      return alert("Please select a value before adding a row.");
    }

    if (carrierrows.some((row) => row.carrier === selectValue)) {
      alert(`${selectValue} has already been added`)
      return
    }
    const newRow = {
      key: carrierrows.length,
      carrier: selectValue,
      percent: 100,
    };

    setCarrierrows([...carrierrows, newRow]);
    setSelectValue("")
  };

  const handleDeleteRow = (key: number) => {
    setCarrierrows(carrierrows.filter((row) => row.key !== key));
  };

  const handleOk = () => {
    const total = formatNumber(carrierrows.reduce((sum, row) => sum + Number(row.percent), 0));

    if (total === 100) {
      if (currentRowIndex !== null) {
        const tempcarriers: { name: string, NAIC: string, percent: number }[] = []

        carrierrows.map((carrier) => {
          tempcarriers.push({ name: carrier.carrier, NAIC: '', percent: carrier.percent })
        })
        console.log(tempcarriers)
        const updateFiling = filingList[currentRowIndex]
        updateFiling.carriers = tempcarriers

        UpdateFiling(updateFiling)
          .then((AddFilingResult) => {
            if (AddFilingResult.success) {
              if (isAddFilingResponse(AddFilingResult)) {
                const updatedfilinglist = [...filingList];
                updatedfilinglist[currentRowIndex] = AddFilingResult.newfiling
                setFilingList(updatedfilinglist);
                //setRowData(convertToRowFiling(updatedfilinglist, slcodes))
                const rowNode = gridRef.current!.api.getDisplayedRowAtIndex(currentRowIndex)
                rowNode?.setData(convertToRow(AddFilingResult.newfiling, slcodes))
              }
            }
            else {
              if ("errorMsg" in AddFilingResult) {
                message.error(AddFilingResult.errorMsg)
              }
            }
          })
          .catch((error) => {
            console.error("An error occurred:", error.message);
            setIsModalVisible(false);
            setCarrierrows([]);
            return
          });
      }
      setIsModalVisible(false);
      setCarrierrows([]);
    } else {
      // alert("The carrier total must equal 100% - please correct it");
      alert(`The carrier total is now ${total}% - it must equal 100%`);
    }
  };

  const handleRowDelete = (deletedRow: number) => {
    console.log(`delete = ${listRef.current[deletedRow].id}`)
    RemoveFiling(listRef.current[deletedRow].id)
      .then((result) => {

        if (result.success) {
          listRef.current.splice(deletedRow, 1)
          setFilingList(listRef.current);
          setRowData(convertToRowFiling(listRef.current, slcodes))
          //ReloadFiling(selectUser)
          message.success(result.errorMsg)
        }
        else {
          message.error(result.errorMsg)
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
        return
      });
  };

  const handlePayUrlBtn = () => {
    getPaymentUrl()
      .then((response) => {
        if (response.success) {
          console.log(`get url = ${response.msg}`)
          window.open(response.msg, '_blank');
        }
        else {
          message.error(response.msg)
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
        return
      });
  }

  const DownloadFilingExcel = (bshort: boolean) => {
    const excelrequest: ExcelFilingRequest = {
      fetchRequest: {
        userid: Number(selectUser),
        posted: "N",
        searchWords: "",
        licenseNumber: user.licenseNumber
      },
      bShort: bshort
    }
    message.loading({ content: 'Excel File Downloading...', key: 'downloadexcel', duration: 0 });
    DownloadExcelFiling(excelrequest)
      .then((excelFilingResult) => {
        if (excelFilingResult.success) {
          message.success({ content: excelFilingResult.errorMsg, key: 'downloadexcel', duration: 2 })
        }
        else {
          message.error({ content: excelFilingResult.errorMsg, key: 'downloadexcel', duration: 2 })
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
        return
      });

  }

  const showBulkHelpModal = () => {
    const fetchBulkHelpHtml = async () => {
      try {
        const response = await fetch('/html/bulkhelp.html');
        if (response.ok) {
          const htmlContent = await response.text();
          setBulkHelp_html(DOMPurify.sanitize(htmlContent));
        } else {
          console.error('Failed to load aboutus.html');
        }
      } catch (error) {
        console.error('Error fetching aboutus.html:', error);
      }
    };

    fetchBulkHelpHtml()
    setIsBulkHelpModal(true);
  }


  const ShortFormExcel = () => {
    DownloadFilingExcel(true)
  }

  const LongFormExcel = () => {
    DownloadFilingExcel(false)
  }

  const handleNewFilingBtn = () => {
    //const updatedRowData = [...rowData, defaultFiling];
    //setRowData(updatedRowData);
    const date = new Date("1/1/2000")
    const pstDate = date
    console.log(pstDate);
    const updateData: FilingData = {
      id: 0,
      brokerID: 0,
      userID: 0,
      licenseNumber: "",
      filingMethod: "",
      sourceDocument: "",
      insuredName: "",
      insuredAddress: "",
      policyNumber: "new",
      transType: "",
      transDate: new Date(),
      transEffectiveDate: pstDate,
      policyEffectiveDate: pstDate,
      late: "",
      slCode: "",
      premium: 0,
      policyFee: 0,
      tax: 0,
      stampingFee: 0,
      latefee: 0,
      warning: "",
      warningMessage: "",
      period:"",
      carriers: []
    }

    AddFiling(updateData)
      .then((AddFilingResult) => {
        if (AddFilingResult.success) {
          if (isAddFilingResponse(AddFilingResult)) {
            const updatedfilinglist = [...filingList, AddFilingResult.newfiling];
            setFilingList(updatedfilinglist);
            setRowData(convertToRowFiling(updatedfilinglist, slcodes))
          }
          //ReloadFiling(selectUser)
        }
        else {
          if ("errorMsg" in AddFilingResult) {
            message.error(AddFilingResult.errorMsg)
          }
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
        return
      });
  }

  interface DataType {
    key: string;
    carrier: string;
    percent: number;
  }
  const columns: ColumnsType<DataType> = [
    {
      title: "Carrier",
      dataIndex: "carrier",
      key: "carrier",
      width: "400px",
      align: "center"
    },
    {
      title: ( <div style={{ textAlign: "center" }}> Percent {carrierrows.length > 0 && (
                <>
                  <br />
                  <span style={{ fontWeight: "normal", fontSize: 12 }}>{formatNumber(carrierrows.reduce((sum, row) => sum + Number(row.percent), 0)).toString()}%</span>
                </>
              )} </div> ),
      dataIndex: "percent",
      key: "percent",
      width: "60px",
      align: 'right',
      render: (_: any, record: any) => (
        <input
          type="number"
          min={0}
          max={100}
          value={record.percent}
          style={{textAlign: 'right'}}
          onChange={(e) => {
            const updatedRows = carrierrows.map((row) =>
              row.key === record.key ? { ...row, percent: e.target.value } : row
            );
            setCarrierrows(updatedRows);
          }}
        />
      ),
    },
    {
      title: "Delete",
      dataIndex: "delete",
      key: "delete",
      width: "60px",
      render: (_: any, record: any) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDeleteRow(record.key)}
        />
      ),
    },
  ];

  const showPostModal = () => {
    setIsPostModal(true)
  }

  const PostFilingProc = (bLateProcess: boolean) => {

    const postfiling: PostFilingRequest = {
      bLateProcess: bLateProcess
    }
    setLoading(true);
    setLoadingMessage('Posting...');
    PostFiling(postfiling)
      .then((response) => {
        if (response.success) {
          ReloadFiling(user.userid)
          //message.success(response.msg)
        }
        else {
          //message.error(response.msg)
        }
      })
      .catch((error) => {
        console.error("An error occurred:", error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handlePostOk = () => {
    PostFilingProc(true)
    setIsPostModal(false)
  }

  const handlePostCancel = () => {
    setIsPostModal(false)
  }

  const handlePostBtnClick = () => {

    if (Boolean(user.locked)) {
      notification.warning({
        message: "Account Locked",
        description: "This account is restricted - you will not be able to post new transactions.  Please call our office at 602-279-6344.",
      });
      return;
    }


    if (selectUser === "0") {
      const modal = confirm({
        title: "We can't post for all users, but we can move the other users filings to your new filings page so they can be posted",
        icon: <ExclamationCircleFilled />,

        footer: [
          <div className='text-right mt-5'>
            <Button key="ok" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
              onClick={() => {
                modal.destroy();  // Close modal only when clicked
                api_ChangeUserID()
                  .then((response) => {
                    if (response.success) {
                      message.success(response.errorMsg)
                    }
                    else {
                      message.error(response.errorMsg)
                    }
                  })
                  .catch((error) => {
                    console.error("An error occurred:", error.message);
                    return
                  });
              }}>
              Yes
            </Button>,
            <Button key="cancel" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
              onClick={() => {
                console.log('Cancel');
                modal.destroy();  // Close modal only when clicked
              }}>
              No
            </Button>
          </div>
        ],
      });
      return
    }
    if (selectUser !== user.userid.toString()){
      message.error("Please select your user before posting")
      return
    }

    var bRes = false

    filingList.forEach((item) => {
      
      if (item.warning.length) {
        bRes = true
      }
    });

    if (bRes)
    {
      Modal.confirm({
      title: 'Invalid Lines',
      content: 'There are lines with errors - do you want to continue posting?',
      okText: 'Yes',
      // cancelButtonProps: { style: { display: 'none' } },
      cancelText:'No',
      onOk: () => {
        bRes = false
        filingList.forEach((item) => {
          if (item.warning.length === 0 && item.late.length) {
            bRes = true
          }
        });
        if (bRes) {
          showPostModal()
        }
        else {
          PostFilingProc(false)
        }
      },
      });
    }
    else{
      bRes = false
      filingList.forEach((item) => {
        if (item.late.length) {
          bRes = true
        }
      });
      if (bRes) {
        showPostModal()
      }
      else {
        PostFilingProc(false)
      }
    }
  }

  const onColumnHeaderClicked = (event: any) => {
    const colId = event.column.getColId();
    if (colId === 'delete') {
      const modal = confirm({
        title: 'Do you want to delete all the entries?',
        icon: <ExclamationCircleFilled />,

        footer: [
          <div className='text-right mt-5'>
            <Button key="ok" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
              onClick={() => {
                modal.destroy();  // Close modal only when clicked
                DeleteAllFilings()
                  .then((response) => {
                    if (response.success) {
                      message.success(response.msg)
                      setFilingList([]);
                      setRowData([]);
                    }
                    else {
                      message.error(response.msg)
                    }
                  })
                  .catch((error) => {
                    console.error("An error occurred:", error.message);
                    return
                  });
              }}>
              Yes
            </Button>,
            <Button key="cancel" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
              onClick={() => {
                console.log('Cancel');
                modal.destroy();  // Close modal only when clicked
              }}>
              No
            </Button>
          </div>
        ],
      });
    }
  };

  const handleFileUpload = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click(); // Trigger file input click
    }
  };

  const showOverwriteConfirm = async (formData: FormData) => {

    const modal = confirm({
      title: 'Warning!!!',
      icon: <ExclamationCircleFilled />,
      content: 'This file has been uploaded before. If you didn\'t end up saving these filings, you can continue. If you did save the filings, don\'t continue.\n Do you want to continue?',
      footer: [
        <div className='text-right mt-5'>
          <Button key="ok" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
            onClick={() => {
              modal.destroy();
              formData.set('overwrite', 'true');

              try {
                message.loading({ content: 'Uploading...', key: 'uploadStatus', duration: 0 });
                uploadexcel(formData)
                  .then((response) => {
                    if (response.success && isUploadExcelResponse(response)) {
                      console.log(`upload response code: ${response.code}`)
                      switch (response.code) {
                        case 0:
                          processUploadResponse(response);
                          break;
                        case 1:
                          message.warning({ content: 'You can\'t do a bulk upload until all the entries in New Filings are posted or removed', key: 'uploadStatus', duration: 8 });
                          break;
                        case 3:
                          message.error({ content: 'File parsing error', key: 'uploadStatus', duration: 5 });
                          break;
                        case 4:
                          message.error({ content: 'Invalid License Number', key: 'uploadStatus', duration: 5 });
                          break;
                        case 5:
                          message.error({ content: 'Invalid carrier information. We sent more detailed information to you by email. Please review it there', key: 'uploadStatus', duration: 5 });
                          break;
                        case 6:
                          message.error({ content: 'Sorry, this file could not be uploaded as it has errors.\n  Please check your email for more details', key: 'uploadStatus', duration: 5 });
                          break;
                        default:
                          message.error({ content: 'Unknown response code', key: 'uploadStatus', duration: 2 });
                      }
                    } else {
                      message.error({ content: 'Failed to upload.', key: 'uploadStatus', duration: 2 });
                    }
                  })
              } catch (error) {
                handleUploadError(error);
              }
            }}>
            Yes
          </Button>,
          <Button key="cancel" size='small' style={{ width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' }}
            onClick={() => {
              console.log('Cancel');
              message.destroy('uploadStatus')
              modal.destroy();  // Close modal only when clicked
            }}>
            No
          </Button>
        </div>
      ],
    });

  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('overwrite', 'false');

    try {
      message.loading({ content: 'Uploading...', key: 'uploadStatus', duration: 0 });
      const response = await uploadexcel(formData);

      if (!response.success || !isUploadExcelResponse(response)) {
        message.error({ content: 'Failed to upload.', key: 'uploadStatus', duration: 2 });

      } else {
        console.log(`upload response code: ${response.code}`)
        switch (response.code) {
          case 0:
            processUploadResponse(response);
            break;
          case 1:
            message.warning({ content: 'You can\'t do a bulk upload until all the entries in New Filings are posted or removed', key: 'uploadStatus', duration: 2 });
            break;
          case 2:
            showOverwriteConfirm(formData);
            break;
          case 3:
            message.error({ content: 'File parsing error', key: 'uploadStatus', duration: 5 });
            break;
          case 4:
            message.error({ content: 'Invalid License Number', key: 'uploadStatus', duration: 5 });
            break;
          case 5:
            message.error({ content: 'Invalid carrier information.\n We sent more detailed information to you by email. Please review it there', key: 'uploadStatus', duration: 5 });
            break;
          case 6:
            message.error({ content: 'Sorry, this file could not be uploaded as it has errors. \n Please check your email for more details', key: 'uploadStatus', duration: 5 });
            break;
          default:
            message.error({ content: 'Unknown response code', key: 'uploadStatus', duration: 2 });
        }
      }
    } catch (error) {
      handleUploadError(error);
    }
  };


  // Helper function to process the upload response
  const processUploadResponse = (response: UploadExcelResponse): void => {
    if (response.filings.length === 0) {
      message.error({ content: `0 Filings Uploaded`, key: 'uploadStatus', duration: 5 });
      setFilingList([]);
      setRowData([]);
    } else {
      console.log(response.filings.length)
      message.success({ content: `${response.filings.length} Filings Uploaded`, key: 'uploadStatus', duration: 5 });
      setFilingList(response.filings);
      setRowData(convertToRowFiling(response.filings, slcodes));
    }
  };

  // Helper function to handle upload errors
  const handleUploadError = (error: unknown): void => {
    console.error('Error:', error instanceof Error ? error.message : error);
    message.error({ content: 'Failed to upload.', key: 'uploadStatus', duration: 2 });
  };

  const handleImport = () => {
    if (!policynumber) {
      message.error({ content: 'Please enter a policy number.', key: 'importStatus', duration: 2 });
      return;
    }

    // Call the import function with the policy number
    api_getFiling(policynumber)
      .then((response) => {
        if (response.success) {
          setCarrierrows([]);
          if ('filing' in response) {
            const filing = response.filing;
            filing.carriers.map((carrier, index) => {
              setCarrierrows((prevRows) => [
                ...prevRows,
                {
                  key: index,
                  carrier: carrier.name,
                  percent: carrier.percent,
                },
              ]);
            });
          }
        } else {
          message.error({ content: 'errorMsg' in response ? response.errorMsg : 'Fail to import.', key: 'importStatus', duration: 2 });
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        message.error({ content: 'Import failed.', key: 'importStatus', duration: 2 });
      });
  }

  const onUserSelectChange = (selectedValue: string) => {
    setSelectUser(selectedValue);
    ReloadFiling(selectedValue)
  }

  return (
    <div style={{ height: '100%' }} className='text-style'>
      <div className="flex justify-between items-center bg-gray-100" style={HeaderStyle}>
        <div className='flex flex-row ml-5 text-xs'>
          <Button
            variant="outlined"
            type='primary'
            onClick={handleFileUpload}
            icon={<UploadOutlined className='icon-style' />}
            className={`ml-0.5 mr-1.5 button-style ${Number(user.allowBulkUpload) !== 1 ? 'hidden' : ''}`}>
            Bulk Upload
          </Button>
          <Button
            type='primary'
            onClick={showBulkHelpModal}
            variant="outlined"
            icon={<QuestionCircleOutlined className='icon-style' />}
            className={`button-style ${Number(user.allowBulkUpload) !== 1 ? 'hidden' : ''}`}>
            Help
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls, .csv"
            style={{ display: 'none' }} // Hide the file input
            onChange={handleFileChange}
          />
        </div>
        <div className="flex flex-row-reverse bg-gray-100 items-center">
          <Button
            variant="outlined"
            type='primary'
            onClick={handlePayUrlBtn}
            icon={<CreditCardFilled className='icon-style' />}
            className='ml-0.5, mr-1.5 button-style'>
            Pay Online
          </Button>
          <Button
            variant="outlined"
            type='primary'
            onClick={handlePostBtnClick}
            icon={<SendOutlined className='icon-style' />}
            className='ml-0.5, mr-1.5 button-style'>
            Post Filings
          </Button>
          <Popconfirm
            placement="bottom"
            overlayStyle={{ width: 300, height: 100, fontSize: 12 }}
            icon={null}
            title="Long or Short Form?"
            description="The short form is ideal for printing but does not include full details, the long form includes all details about the filing but does not print well."
            okButtonProps={{ size: "small", style: { width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' } }}
            cancelButtonProps={{ size: "small", style: { width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' } }}
            okText="Short Form"
            cancelText="Long Form"
            onConfirm={ShortFormExcel}
            onCancel={LongFormExcel}
          >
            <Button
              variant="outlined"
              type='primary'
              icon={<FileExcelOutlined className='icon-style' />}
              className='ml-0.5, mr-1.5 button-style'>
              Save to Excel
            </Button>
          </Popconfirm>
          <Button
            variant="outlined"
            type='primary'
            onClick={handleNewFilingBtn}
            icon={<PlusOutlined className='icon-style' />}
            className='ml-0.5, mr-1.5 button-style'>
            New Filing
          </Button>
          <Select
              defaultValue={user.name}
              style={{ width: 140, height: 22}}
              dropdownStyle={{ borderRadius: 0 }}
              className='ml-0.5, mr-1.5 rectangular-select'
              onChange={onUserSelectChange}
              options={Object.entries(users).map(([key, value]) => ({
                  value: key, 
                  label: value
                }))}
          />
                <span className='mr-1.5 text-xs'>User: </span>
        </div>
      </div>

      <div className="ag-theme-alpine tab-container">
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={colDefs}
          theme={myTheme}
          defaultColDef={defaultColDef}
          headerHeight={35}
          rowHeight={25}
          onCellValueChanged={onCellValueChanged}
          onCellDoubleClicked={onCellDoubleClicked}
          components={{ customTooltip: CustomTooltip }}
          onGridReady={onGridReady}
          //onGridReady={(params) => params.api.sizeColumnsToFit()}
          onColumnHeaderClicked={onColumnHeaderClicked}
          tooltipShowDelay={0}
          tooltipHideDelay={2000000}
          stopEditingWhenCellsLoseFocus={true}
          loading={loading}
          loadingOverlayComponent={CustomLoadingOverlay}
          loadingOverlayComponentParams={{ message: loadingMessage }}
        />
      </div>

      <Modal
        title={
          <div className='text-style' style={{ textAlign: "center", fontWeight: "bold" }}>
            Carrier Allocation
          </div>
        }
        className='text-style'
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleOk}
        width={600}
        height={400}
        footer={[
          <div className="flex flex-row justify-end" key="footer" style={{ width: "100%" }}>
            {/* <div className="flex items-center gap-2">
              <span>Policy Number:</span>
              <Input className="w-28 h-6" onChange={(e) => setPolicyNumber(e.target.value)} />
              <Button className="h-7" type="primary" onClick={handleImport}>
                Import
              </Button>
            </div> */}
            <div className="flex items-center gap-2">
              <Button key="submit" className="h-7 w-20" type="primary" onClick={handleOk}>
                Save
              </Button>
              <Button key="cancel" className="h-7 w-20" type="primary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        ]}
      >
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <Select
            showSearch
            placeholder="Select an option"
            size="small"
            value={selectValue}
            className="fixed-select"
            options={carriers.map((carrier, index) => ({
              key: index,
              value: carrier,
            }))}
            onChange={(value) => setSelectValue(value)}
          >
          </Select>
          <Button type="primary" className='ml-2' style={{ height: 25 }} onClick={handleAddRow}>
            Add
          </Button>
          <Popconfirm
            placement="bottom"
            overlayStyle={{ width: 300, height: 100, fontSize: 12 }}
            icon={null}
            title={<span className="text-style font-bold text-sm">
              Import carriers from policy number
            </span>}
            // description="Enter a policy number and the carrier settings for that policy will be used.  This will overwrite any carriers that you may have already added.  Press Cancel if you want to continue adding manually"
            description={
              <div className="text-style mt-2">
                <p>Enter a policy number and the carrier settings for that policy will be used.  This will overwrite any carriers that you may have already added.  
                  <br />Press Cancel if you want to continue adding manually
                </p>
                <div className="flex flex-row items-center gap-2 ml-5 mb-2">
                  <span>Policy Number:</span>
                  <Input className="w-36 h-6" onChange={(e) => setPolicyNumber(e.target.value)} />
                </div>
              </div>
            }
            okButtonProps={{ size: "small", style: { width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' } }}
            cancelButtonProps={{ size: "small", style: { width: 80, fontSize: 12, backgroundColor: 'green', color: 'white' } }}
            okText="Cancel"
            cancelText="Import"
            onConfirm={() => {}}
            onCancel={handleImport}
          >
            <Button
              variant="outlined"
              type='primary'
              className="ml-2" style={{ height: 25 }}>
              Import from Policy
            </Button>
          </Popconfirm>
        </div>

        <Table
          columns={columns}
          dataSource={carrierrows}
          pagination={false}
          bordered
          size="small"
          // footer={() => 
          //   <div className="flex flex-row">
          //     <div className="flex ml-[350px]">
          //       Total: 
          //     </div> 
          //     <div className="flex ml-6">
          //       {carrierrows.reduce((sum, row) => sum + Number(row.percent), 0)}%
          //     </div>
          //   </div>}
          rowKey="key"
        />
      </Modal>
      <Modal
        className='text-style'
        open={isPostModal}
        onCancel={handlePostCancel}
        onOk={handlePostOk}
        width={500}
        height={300}
        footer={[
          <Button key="submit" type="primary" onClick={handlePostOk} style={{ width: "100px", height: "25px" }}>
            Yes
          </Button>,
          <Button key="submit" type="primary" onClick={handlePostCancel} style={{ width: "100px", height: "25px" }}>
            No
          </Button>,
        ]}
      >
        <p>There are late entries in this set of filings - do you want to proceed with posting?</p>
        {/* <div className='flex, flex-col'>
                <div style={{fontSize: "12px", textAlign: "center", fontWeight: "bold"}}>Arizona Department Of Insurance </div>
                <div style={{fontSize: "12px", textAlign: "center", fontWeight: "bold"}}>Surplus Line Broker Affidavit </div>
                <div className='flex flex-row mt-5'>
                  <span className='mt-1' style={{fontSize: "11px"}}>I/We:&nbsp;</span>
                  <Input onChange={handleBrokerNameChange} size='small' style={{width: "100px", height: "20px"}}/>
                  <span className='mt-1' style={{fontSize: "11px"}}>&nbsp; are an Arizona Licensed Surplus Line broker, Arizona Surplus Lines Broker number:&nbsp; </span>
                  <Input onChange={handleLicenseChange} size='small' style={{width: "60px", height: "20px"}}/>
                </div>
                <div className='mt-1' style={{fontSize: "11px"}}>1. I/We am/are submitting to the Surplus Lines Association of Arizona, as agent for the Director of the Department </div>
                <div style={{fontSize: "11px"}}> &nbsp;&nbsp; of Insurance of Arizona, the attached monthly report of insureds for whom I/we have procured coverage in the surplus lines market </div>
                <div className='flex flex-row'>
                  <span style={{fontSize: "11px"}}>&nbsp;&nbsp; in the month of &nbsp;</span>
                  <Input onChange={handleMonthChange} size='small' style={{width: "60px", height: "20px"}}/>
                  <span style={{fontSize: "11px"}}>&nbsp; in the year&nbsp; </span>
                  <Input onChange={handleYearChange} size='small' style={{width: "60px", height: "20px"}}/>
                </div>
                <div style={{fontSize: "11px"}}>2. All or a portion of the insurance coverage sought by each insured risk listed on the monthly report is not procurable from</div>
                <div style={{fontSize: "11px"}}>&nbsp; at least three insurers that have a certificate of authority to transact insurance in Arizona and are authorized to write </div>
                <div style={{fontSize: "11px"}}>&nbsp; the particular insurance coverage type, class, or kind of insurance sought. </div>
                <div style={{fontSize: "11px"}}>3. The placing of insurance with an unauthorized insurer is not for the purpose of securing advantages either </div>
                <div style={{fontSize: "11px"}}>&nbsp;   as to premium rate or terms of the insurance contract.  </div>
                <div style={{fontSize: "11px"}}>4. I/We will maintain evidence of diligent effort described in paragraph 2 in my files for at least six years after the </div>
                <div style={{fontSize: "11px"}}>&nbsp;   expiration of the insurance policy, including at least the following:</div>
                <div style={{fontSize: "11px"}}>&nbsp;&nbsp;A. The names of the insurers, possessing an Arizona certificate of authority, that declined coverage for the risk</div>
                <div style={{fontSize: "11px"}}>&nbsp;&nbsp; listed in the attached monthly report.</div>
                <div style={{fontSize: "11px"}}>&nbsp;&nbsp;B. The names of the insurers' representatives who were contacted and who declined coverage; and </div>
                <div style={{fontSize: "11px"}}>&nbsp;&nbsp;C. The date on which such coverage was declined.</div>
                <div style={{fontSize: "11px"}}>5. The risks named in the attached monthly report have been placed with a surplus lines insurer currently </div>
                <div style={{fontSize: "11px"}}>&nbsp;   appearing on the Director's List of Surplus Lines Insurers permitted to transact surplus lines insurance in Arizona.</div>
                <div className='flex flex-row mt-3'>
                  <span className='ml-1' style={{fontSize: "11px"}}>&nbsp;&nbsp; Signature : &nbsp;&nbsp;</span>
                  <Input onChange={handleSignatureChange} size='small' style={{width: "100px", height: "20px"}}/>
                  <span className='ml-5' style={{fontSize: "11px"}}>&nbsp; Date : &nbsp;&nbsp;</span>
                  <Input onChange={handleDateChange} size='small' style={{width: "100px", height: "20px"}}/>
                </div>
              </div> */}
      </Modal>
      <Modal
        title={
          <div className='text-style' style={{ textAlign: "center", fontWeight: "bold" }}>
            Bulk Upload Help...
          </div>
        }
        className='text-style'
        open={isBulkHelpModal}
        width={400}
        height={300}
        onCancel={handleBulkHelpCancel}
        footer={null}
      >
        <div style={bulkhelpStyle}>
          <div dangerouslySetInnerHTML={{ __html: bulkhelp_html }} />
        </div>
      </Modal>
    </div>
  )
}

export default NewFiling;